import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AlertListOptions, alertsService } from '@/services';
import { AlertDeliveryDestinationProvider, AlertInput, WorkspaceAlert } from '@/types';

const ALERTS_QUERY_KEY = ['workspace-alerts'] as const;
const ALERTS_CATALOG_QUERY_KEY = [...ALERTS_QUERY_KEY, 'catalog'] as const;

export const useAlerts = ({ limit = 100, page = 1, search = '' }: AlertListOptions = {}, enabled = true) =>
    useQuery({
        enabled,
        queryFn: () => alertsService.list({ limit, page, search }),
        queryKey: [...ALERTS_QUERY_KEY, 'list', page, limit, search],
        staleTime: 30_000,
    });

export const useAlert = (alertId?: string, initialData?: WorkspaceAlert, enabled = true) =>
    useQuery({
        enabled: enabled && Boolean(alertId),
        placeholderData: initialData,
        queryFn: () => alertsService.get(alertId!),
        queryKey: [...ALERTS_QUERY_KEY, 'detail', alertId],
        staleTime: 15_000,
    });

export const useAlertsCatalog = (enabled = true) =>
    useQuery({ enabled, queryFn: alertsService.catalog, queryKey: ALERTS_CATALOG_QUERY_KEY, staleTime: 300_000 });

export const useAlertDeliveryDestinations = (provider: AlertDeliveryDestinationProvider, enabled = true) =>
    useQuery({
        enabled,
        queryFn: () => alertsService.deliveryDestinations(provider),
        queryKey: [...ALERTS_QUERY_KEY, 'delivery-destinations', provider],
        staleTime: 60_000,
    });

const useInvalidateAlerts = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
};

export const useCreateAlert = () => {
    const invalidate = useInvalidateAlerts();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: alertsService.create,
        onSuccess: (alert) => {
            queryClient.setQueryData([...ALERTS_QUERY_KEY, 'detail', alert.id], alert);
            void invalidate();
        },
    });
};

export const useTestAlert = () => useMutation({ mutationFn: alertsService.test });

export const useUpdateAlert = () => {
    const invalidate = useInvalidateAlerts();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ alertId, input, revision }: { alertId: string; input: Partial<AlertInput>; revision: number }) =>
            alertsService.update(alertId, input, revision),
        onSuccess: (alert) => {
            queryClient.setQueryData([...ALERTS_QUERY_KEY, 'detail', alert.id], alert);
            void invalidate();
        },
    });
};

export const useDeleteAlert = () => {
    const invalidate = useInvalidateAlerts();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ alertId, revision }: { alertId: string; revision: number }) =>
            alertsService.delete(alertId, revision),
        onSuccess: (_response, { alertId }) => {
            queryClient.removeQueries({ queryKey: [...ALERTS_QUERY_KEY, 'detail', alertId] });
            void invalidate();
        },
    });
};
